<template>
  <div class="info-section">
    <h4 class="gm-label">Labels</h4>

    <div class="labels-container">
      <!-- Kind: what the room is in the campaign's shape -->
      <div class="label-group">
        <span class="group-eyebrow">Kind</span>
        <div class="chip-row">
          <button
              v-for="entry in kinds"
              :key="entry.value"
              :class="['kind-chip', {'is-active': entry.value === kind}]"
              :disabled="!editable || busy"
              :title="entry.description"
              type="button"
              @click="toggleKind(entry.value)"
          >{{ entry.title }}</button>
        </div>
      </div>

      <!-- Marker: the icon drawn on the map -->
      <div class="label-group">
        <span class="group-eyebrow">Map marker</span>
        <div class="marker-grid">
          <button
              v-for="entry in markers"
              :key="entry.value"
              :class="['marker-tile', {'is-active': entry.value === marker}]"
              :disabled="!editable || busy"
              :title="entry.description"
              type="button"
              @click="selectMarker(entry.value)"
          >
            <span class="marker-art">
              <img
                  v-if="iconUrl(entry.icon)"
                  :alt="''"
                  :src="iconUrl(entry.icon)"
                  class="marker-img"
              />
              <svg v-else class="marker-none" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="8"/>
                <path d="M6.5 17.5l11-11"/>
              </svg>
            </span>
            <span class="marker-name">{{ entry.title }}</span>
          </button>
        </div>
      </div>

      <!-- Name: the only place a room's name is stored -->
      <div class="label-group">
        <span class="group-eyebrow">Room name</span>
        <input
            v-model="nameDraft"
            :disabled="!editable || busy"
            class="gm-input name-input"
            placeholder="Unnamed room"
            @blur="commitName"
            @keyup.enter="commitName"
        />
      </div>

      <!-- Free tags: anything the engine does not read -->
      <div class="label-group">
        <span class="group-eyebrow">Tags</span>
        <div v-if="tags.length" class="tag-row">
          <span v-for="tag in tags" :key="tag" class="tag-chip">
            {{ tag }}
            <button
                v-if="editable"
                :disabled="busy"
                class="tag-remove"
                title="Remove this tag"
                type="button"
                @click="$emit('remove-label', tag)"
            >
              <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4.5 4.5l7 7M11.5 4.5l-7 7"/></svg>
            </button>
          </span>
        </div>
        <p v-else class="empty-note">No tags.</p>

        <div v-if="editable" class="tag-add">
          <input
              v-model="tagDraft"
              :disabled="busy"
              class="gm-input tag-input"
              placeholder="Add a tag"
              @keyup.enter="commitTag"
          />
          <button
              :disabled="busy || !tagDraft.trim()"
              class="gm-btn gm-btn--sm"
              type="button"
              @click="commitTag"
          >
            <svg class="btn-glyph" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 3.5v9M3.5 8h9"/></svg>
            Add
          </button>
        </div>
      </div>

      <!-- What the engine will do when two labels disagree -->
      <ul v-if="conflicts.length" class="conflict-list">
        <li
            v-for="(conflict, index) in conflicts"
            :key="`${conflict.code}-${index}`"
            :class="['conflict', `conflict--${conflict.severity}`]"
        >
          <span class="conflict-dot"></span>
          <span class="conflict-text">{{ conflict.message }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import {roomIconUrl} from '@/utils/roomIcons';

const KIND_SLOT = 0;
const MARKER_SLOT = 1;
const NAME_SLOT = 2;

export default {
  name: 'Labels',
  props: {
    labels: {
      type: Array,
      default: () => []
    },
    catalog: {
      type: Object,
      default: null
    },
    conflicts: {
      type: Array,
      default: () => []
    },
    editable: {
      type: Boolean,
      default: false
    },
    busy: {
      type: Boolean,
      default: false
    }
  },
  emits: ['add-label', 'remove-label', 'set-labels'],
  data() {
    return {
      nameDraft: this.readSlot(this.labels, NAME_SLOT),
      tagDraft: ''
    };
  },
  computed: {
    kinds() {
      return this.catalog?.kinds || [];
    },
    markers() {
      return this.catalog?.markers || [];
    },
    kind() {
      return this.readSlot(this.labels, KIND_SLOT);
    },
    marker() {
      return this.readSlot(this.labels, MARKER_SLOT);
    },
    tags() {
      return (this.labels || [])
          .slice(NAME_SLOT + 1)
          .map(label => (label || '').trim())
          .filter(Boolean);
    }
  },
  watch: {
    labels: {
      handler(newLabels) {
        this.nameDraft = this.readSlot(newLabels, NAME_SLOT);
      },
      deep: true
    }
  },
  methods: {
    readSlot(labels, slot) {
      const value = (labels || [])[slot];
      return typeof value === 'string' ? value.trim() : '';
    },
    iconUrl(icon) {
      return roomIconUrl(icon);
    },
    toggleKind(value) {
      this.$emit(value === this.kind ? 'remove-label' : 'add-label', value);
    },
    selectMarker(value) {
      if (value !== this.marker) {
        this.$emit('add-label', value);
      }
    },
    commitName() {
      const name = this.nameDraft.trim();
      if (name === this.readSlot(this.labels, NAME_SLOT)) {
        return;
      }
      this.$emit('set-labels', {
        kind: this.kind,
        marker: this.marker,
        name,
        tags: this.tags
      });
    },
    commitTag() {
      const value = this.tagDraft.trim();
      if (!value) {
        return;
      }
      this.$emit('add-label', value);
      this.tagDraft = '';
    }
  }
};
</script>

<style scoped>
.labels-container {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-sm);
}

.label-group {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.group-eyebrow {
  font-family: var(--gm-font-display);
  font-size: var(--gm-text-micro);
  letter-spacing: var(--gm-track-eyebrow);
  text-transform: uppercase;
  color: var(--gm-text-dim);
}

/* Kind reads as a single choice among few, so it is a row of chips rather
   than a select the game master has to open to see the options. */
.chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: var(--gm-gap-xs);
}

.kind-chip {
  padding: 0.22rem 0.5rem;
  font-family: var(--gm-font-body);
  font-size: var(--gm-text-micro);
  letter-spacing: var(--gm-track-label);
  text-transform: uppercase;
  color: var(--gm-text-muted);
  background: var(--gm-surface-sunken);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  cursor: pointer;
  transition: color var(--gm-duration) var(--gm-ease),
              border-color var(--gm-duration) var(--gm-ease),
              background var(--gm-duration) var(--gm-ease);
}

.kind-chip:hover:not(:disabled) {
  color: var(--gm-text);
  border-color: var(--gm-border-hi);
}

.kind-chip.is-active {
  color: var(--gm-text-on-accent);
  background: var(--gm-accent);
  border-color: var(--gm-accent-hi);
}

.kind-chip:disabled {
  cursor: default;
  opacity: 0.55;
}

/* The marker is a picture on the map, so it is picked as a picture. */
.marker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(58px, 1fr));
  gap: var(--gm-gap-xs);
}

.marker-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  padding: 0.3rem 0.15rem 0.25rem;
  background: var(--gm-surface-sunken);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  cursor: pointer;
  transition: border-color var(--gm-duration) var(--gm-ease),
              background var(--gm-duration) var(--gm-ease);
}

.marker-tile:hover:not(:disabled) {
  border-color: var(--gm-border-hi);
}

.marker-tile.is-active {
  border-color: var(--gm-accent);
  background: var(--gm-accent-softer);
}

.marker-tile:disabled {
  cursor: default;
  opacity: 0.55;
}

.marker-art {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
}

.marker-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: var(--gm-radius-sm);
}

.marker-none {
  width: 20px;
  height: 20px;
  fill: none;
  stroke: var(--gm-text-dim);
  stroke-width: 1.4;
}

.marker-name {
  font-size: var(--gm-text-micro);
  line-height: 1.1;
  text-align: center;
  color: var(--gm-text-muted);
}

.marker-tile.is-active .marker-name {
  color: var(--gm-text);
}

.name-input,
.tag-input {
  padding: 0.3rem 0.45rem;
  font-size: var(--gm-text-xs);
}

.tag-row {
  display: flex;
  flex-wrap: wrap;
  gap: var(--gm-gap-xs);
}

.tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  padding: 0.18rem 0.2rem 0.18rem 0.45rem;
  font-family: var(--gm-font-mono);
  font-size: var(--gm-text-micro);
  color: var(--gm-text-muted);
  background: var(--gm-surface-sunken);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
}

.tag-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  padding: 0;
  background: transparent;
  border: none;
  color: var(--gm-text-dim);
  cursor: pointer;
  transition: color var(--gm-duration) var(--gm-ease);
}

.tag-remove:hover:not(:disabled) {
  color: var(--gm-danger);
}

.tag-remove svg {
  width: 9px;
  height: 9px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
}

.tag-add {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-xs);
}

.tag-add .tag-input {
  flex: 1;
  min-width: 0;
}

.empty-note {
  margin: 0;
  font-size: var(--gm-text-micro);
  color: var(--gm-text-dim);
}

/* A conflict is the engine about to resolve something silently. It is stated
   in the panel rather than left for the game master to discover in play. */
.conflict-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin: 0;
  padding: 0.4rem 0.45rem;
  list-style: none;
  background: var(--gm-surface-sunken);
  border-radius: var(--gm-radius-sm);
}

.conflict {
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
}

.conflict-dot {
  flex: none;
  width: 5px;
  height: 5px;
  margin-top: 0.32rem;
  border-radius: 50%;
}

.conflict--error .conflict-dot {
  background: var(--gm-danger);
}

.conflict--warning .conflict-dot {
  background: var(--gm-accent);
}

.conflict--info .conflict-dot {
  background: var(--gm-info);
}

.conflict-text {
  font-size: var(--gm-text-micro);
  line-height: 1.35;
  color: var(--gm-text-muted);
}

.btn-glyph {
  width: 11px;
  height: 11px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.6;
  stroke-linecap: round;
}
</style>
